import { cn } from "@/lib/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes } from "react";
import { useSurface, type SurfaceKind } from "./Surface";
import { useElementRef } from "./use-element-ref";

/** Text color roles shared by every typographic primitive. */
export const TEXT_TONES = {
  default: "text-card-text",
  muted: "text-card-text/75",
  subtle: "text-card-text/50",
  disabled: "text-card-text/30",
  primary: "text-primary",
  secondary: "text-secondary",
  success: "text-success",
  danger: "text-danger",
  warning: "text-warning",
  accent: "text-accent",
  "on-primary": "text-primary-foreground",
  "on-media": "text-on-media",
  "on-title-card": "text-title-card-text",
  "warning-on-media": "text-warning-on-media",
  "game-accent": "text-game-gold-deep",
  inherit: "",
} as const;

/** One of the text color roles in `TEXT_TONES`. */
export type TextTone = keyof typeof TEXT_TONES;

const PAGE_SURFACE_TONES: Partial<Record<TextTone, string>> = {
  default: "text-page-text",
  muted: "text-page-text/75",
  subtle: "text-page-text/50",
  disabled: "text-page-text/30",
};

const GAME_SURFACE_TONES: Partial<Record<TextTone, string>> = {
  default: "text-game-ink",
  muted: "text-game-muted",
  subtle: "text-game-ink/50",
  disabled: "text-game-ink/30",
};

const NIGHT_SURFACE_TONES: Partial<Record<TextTone, string>> = {
  default: "text-night-ink",
  muted: "text-night-ink/80",
  subtle: "text-night-ink/60",
  disabled: "text-night-ink/35",
};

const SURFACE_TONES: Record<SurfaceKind, Partial<Record<TextTone, string>>> = {
  page: PAGE_SURFACE_TONES,
  card: {},
  game: GAME_SURFACE_TONES,
  night: NIGHT_SURFACE_TONES,
};

/** Class of `tone` on `surface`: default/muted/subtle/disabled follow the surface, the other roles are absolute. */
export function textToneClass(tone: TextTone, surface: SurfaceKind): string {
  return SURFACE_TONES[surface][tone] ?? TEXT_TONES[tone];
}

/** Font families beyond the storefront face: `game-display` is the prize games' bold display cut (Cabinet Grotesk); an explicit `weight` or `tracking` wins over it. */
export const TEXT_FAMILIES = {
  "game-display": "font-game-display font-bold tracking-game-display",
} as const;

/** One of the families in `TEXT_FAMILIES`; omitted means inherit. */
export type TextFamily = keyof typeof TEXT_FAMILIES;

/** `textToneClass` against the surface from context; `undefined` tone yields no class (inherit). */
export function useTextToneClass(tone: TextTone | undefined): string {
  const surface = useSurface();
  return tone === undefined ? "" : textToneClass(tone, surface);
}

/** Font sizes on the type scale; omitted means inherit. */
export const TEXT_SIZES = {
  "3xs": "text-3xs",
  "2xs": "text-2xs",
  xs: "text-xs",
  sm: "text-sm",
  base: "text-base",
  lg: "text-lg",
  xl: "text-xl",
  "2xl": "text-2xl",
  "3xl": "text-3xl",
  "4xl": "text-4xl",
} as const;

/** The prize games' fixed type sizes (px-based where the game art is drawn in px, so they never scale with the root): `game-micro` 10px stub/initials, `game-eyebrow` 11px "Você ganhou", `game-title` 1.3rem ticket header and wheel hub. */
export const GAME_TEXT_SIZES = {
  "game-micro": "text-game-micro",
  "game-eyebrow": "text-game-eyebrow",
  "game-title": "text-game-title",
} as const;

/** One of the prize-game sizes in `GAME_TEXT_SIZES`; fixed, never responsive. */
export type GameTextSize = keyof typeof GAME_TEXT_SIZES;

/** Font weights available to typographic primitives. */
export const TEXT_WEIGHTS = {
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold",
  extrabold: "font-extrabold",
  black: "font-black",
} as const;

/** `sm:` breakpoint twins of `TEXT_SIZES`, spelled out so Tailwind sees every class. */
const TEXT_SIZES_SM = {
  "3xs": "sm:text-3xs",
  "2xs": "sm:text-2xs",
  xs: "sm:text-xs",
  sm: "sm:text-sm",
  base: "sm:text-base",
  lg: "sm:text-lg",
  xl: "sm:text-xl",
  "2xl": "sm:text-2xl",
  "3xl": "sm:text-3xl",
  "4xl": "sm:text-4xl",
} as const;

/** One step of the type scale. */
export type TextSize = keyof typeof TEXT_SIZES;

/** A size, or a `{ base, sm }` pair that switches at the `sm` breakpoint. */
export type ResponsiveSize<T extends string> = T | { base: T; sm?: T };

function splitResponsive<T extends string>(size: ResponsiveSize<T> | undefined | null): { base?: T; sm?: T } {
  if (size === undefined || size === null) return {};
  return typeof size === "string" ? { base: size } : size;
}

/** Line-height scale shared by Text, Heading and Price `leading` props. */
export const LEADING = {
  none: "leading-none",
  tight: "leading-tight",
  snug: "leading-snug",
  normal: "leading-normal",
  relaxed: "leading-relaxed",
  comfortable: "leading-6",
  loose: "leading-loose",
} as const;

/** Letter-spacing tokens for the `tracking` prop; `normal` drops a family's tracking, `label` is the eyebrow spacing, `ticket`/`ticket-stub` the scratch ticket's. */
export const TRACKING = {
  normal: "tracking-normal",
  wide: "tracking-wide",
  label: "tracking-label",
  ticket: "tracking-ticket",
  "ticket-stub": "tracking-ticket-stub",
} as const;

const ALIGN = { left: "text-left", center: "text-center", right: "text-right" } as const;
const CLAMP = { 1: "line-clamp-1", 2: "line-clamp-2", 3: "line-clamp-3" } as const;

/** Classes of `Heading`: semantic size scale, bold by default, balanced wrapping. */
export const headingVariants = cva("text-balance", {
  variants: {
    size: {
      hero: "text-4xl",
      display: "text-3xl",
      page: "text-2xl",
      headline: "text-headline",
      title: "text-xl",
      section: "text-lg",
      card: "text-base",
    },
    weight: TEXT_WEIGHTS,
    tone: TEXT_TONES,
    leading: LEADING,
    align: ALIGN,
    truncate: { true: "truncate", false: "" },
    clamp: CLAMP,
  },
  defaultVariants: { weight: "bold", tone: "default", truncate: false },
});

const HEADING_SIZE_BY_LEVEL = { 1: "page", 2: "title", 3: "section", 4: "card" } as const;

/** Semantic heading sizes. */
export type HeadingSize = NonNullable<VariantProps<typeof headingVariants>["size"]>;

const HEADING_SIZES_SM: Record<HeadingSize, string> = {
  hero: "sm:text-4xl",
  display: "sm:text-3xl",
  page: "sm:text-2xl",
  headline: "sm:text-headline",
  title: "sm:text-xl",
  section: "sm:text-lg",
  card: "sm:text-base",
};

type HeadingLevel = 1 | 2 | 3 | 4;

/** Props of `Heading`; `level` picks the element, `size` the look (defaults follow the level), `tone` follows the surface. */
export type HeadingProps = Omit<VariantProps<typeof headingVariants>, "clamp" | "size" | "tone"> &
  Omit<HTMLAttributes<HTMLHeadingElement>, "color"> & {
    level?: HeadingLevel;
    size?: ResponsiveSize<HeadingSize>;
    tone?: TextTone;
    clamp?: 1 | 2 | 3;
    family?: TextFamily;
  };

/** Document heading (h1-h4) with a token size; forwards ref and props so Radix `asChild` works. */
export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(function Heading(
  { level = 2, size, weight, tone = "default", leading, align, truncate, clamp, family, className, ...rest },
  ref,
) {
  const Tag = `h${level}` as const;
  const { base, sm } = splitResponsive(size);
  const toneClass = useTextToneClass(tone);
  return (
    <Tag
      {...rest}
      ref={ref}
      className={cn(
        family && TEXT_FAMILIES[family],
        headingVariants({ size: base ?? HEADING_SIZE_BY_LEVEL[level], weight, tone: "inherit", leading, align, truncate, clamp }),
        sm && HEADING_SIZES_SM[sm],
        toneClass,
        className,
      )}
    />
  );
});

/** Classes of `Text`: every axis optional so unset ones inherit from the parent. */
export const textVariants = cva("", {
  variants: {
    size: { ...TEXT_SIZES, ...GAME_TEXT_SIZES },
    weight: TEXT_WEIGHTS,
    tone: TEXT_TONES,
    leading: LEADING,
    tracking: TRACKING,
    align: ALIGN,
    truncate: { true: "truncate", false: "" },
    clamp: CLAMP,
    tabular: { true: "tabular-nums", false: "" },
    italic: { true: "italic", false: "" },
    uppercase: { true: "uppercase", false: "" },
    balance: { true: "text-balance", false: "" },
  },
  defaultVariants: { tone: "inherit", truncate: false, tabular: false, italic: false, uppercase: false, balance: false },
});

/** Elements `Text` may render as. */
export type TextElement = "p" | "span" | "div" | "label" | "small" | "strong" | "em" | "dt" | "dd" | "li" | "time";

/** Props of `Text`; `htmlFor` applies to labels, `dateTime` to time elements; no `tone` inherits; `balance` evens short multi-line copy; game sizes are prize-game only. */
export type TextProps = Omit<VariantProps<typeof textVariants>, "clamp" | "size" | "tone"> &
  Omit<HTMLAttributes<HTMLElement>, "color"> & {
    as?: TextElement;
    size?: ResponsiveSize<TextSize> | GameTextSize;
    tone?: TextTone;
    clamp?: 1 | 2 | 3;
    htmlFor?: string;
    dateTime?: string;
    family?: TextFamily;
  };

/** Body copy: polymorphic text with token size, weight, tone and leading. */
export const Text = forwardRef<HTMLElement, TextProps>(function Text(
  { as: Tag = "p", size, weight, tone, leading, tracking, align, truncate, clamp, tabular, italic, uppercase, balance, family, className, ...rest },
  ref,
) {
  const elementRef = useElementRef(ref);
  const { base, sm } = typeof size === "object" && size !== null ? size : { base: size, sm: undefined };
  const toneClass = useTextToneClass(tone);
  return (
    <Tag
      {...rest}
      ref={elementRef}
      className={cn(
        family && TEXT_FAMILIES[family],
        textVariants({ size: base, weight, tone: "inherit", leading, tracking, align, truncate, clamp, tabular, italic, uppercase, balance }),
        sm && TEXT_SIZES_SM[sm],
        toneClass,
        className,
      )}
    />
  );
});

/** Props of `Eyebrow`. */
export type EyebrowProps = Omit<HTMLAttributes<HTMLElement>, "color"> & {
  as?: "p" | "span" | "div";
  tone?: TextTone;
  /** `xs` (default) above a title; `sm` captions a form field beside sm-size labels. */
  size?: "xs" | "sm";
};

/** Small uppercase label above a title. */
export const Eyebrow = forwardRef<HTMLElement, EyebrowProps>(function Eyebrow(
  { as = "p", tone = "subtle", size = "xs", className, ...rest },
  ref,
) {
  return (
    <Text
      {...rest}
      ref={ref}
      as={as}
      size={size}
      weight="semibold"
      tone={tone}
      uppercase
      className={cn("tracking-wide", className)}
    />
  );
});
